// v2.22 file master desain: pure display/validation helpers (no imports, so Jest can test them).
// The server checks everything again (type by the first bytes, size, rights); these only give a quick,
// clear message in the browser before anything is sent.

export const JENIS_EKSTENSI = { cdr: "cdr", ai: "ai", psd: "psd", pdf: "pdf", png: "png", tif: "tif", tiff: "tif", jpg: "jpg", jpeg: "jpg", eps: "eps" };
export const NAMA_JENIS = { cdr: "CorelDRAW", ai: "Illustrator", psd: "Photoshop", pdf: "PDF", png: "PNG", tif: "TIFF", jpg: "JPG", eps: "EPS" };
const MIB = 1024 * 1024;

export function jenisDari(nama) {
  const s = String(nama || "");
  const i = s.lastIndexOf(".");
  return i > 0 ? JENIS_EKSTENSI[s.slice(i + 1).toLowerCase()] || null : null;
}

// 0 -> "0 B", 1536 -> "1,5 KB", 117440512 -> "112 MB", 1610612736 -> "1,5 GB" (1024-based, like the server limit)
export function ukuranTeks(byte) {
  const b = Math.max(0, Number(byte) || 0);
  const satuan = [["GB", MIB * 1024], ["MB", MIB], ["KB", 1024]];
  for (const [nama, n] of satuan) {
    if (b >= n) {
      const v = b / n;
      return `${v.toLocaleString("id-ID", { maximumFractionDigits: v >= 100 ? 0 : 1 })} ${nama}`;
    }
  }
  return `${b} B`;
}

const ARSIP = /\.(rar|zip|7z|tar|gz|tgz)$/i;

// v2.22.2: system / hidden files a folder brings along (Windows, macOS, Office lock files): skipped, not refused.
// nama = file name, jalur = path inside the chosen folder ("" for a single file)
const NAMA_SISTEM = new Set(["thumbs.db", "ehthumbs.db", "desktop.ini", ".ds_store", "icon\r"]);
export function fileSistem(nama, jalur = "") {
  const n = String(nama || "");
  if (NAMA_SISTEM.has(n.toLowerCase()) || n.startsWith(".") || n.startsWith("~$")) return true;
  return String(jalur || "").split("/").some((b) => b === "__MACOSX" || b === "$RECYCLE.BIN" || (b.startsWith(".") && b.length > 1));
}

// null = may be uploaded; otherwise the message to show (batas = response "batas" of GET /spks/{id}/file-master)
export function cekPilihan(file, batas) {
  if (!file) return "Pilih file dulu.";
  const jenis = jenisDari(file.name);
  const boleh = (batas?.jenis || []).map((j) => NAMA_JENIS[j] || j).join(", ");
  if (!jenis && ARSIP.test(String(file.name || ""))) return "File arsip (RAR/ZIP/7Z) tidak diterima. Ekstrak dulu, lalu unggah file desainnya (bisa sekaligus satu folder).";
  if (!jenis || !(batas?.jenis || []).includes(jenis)) return `Jenis file tidak diterima. Yang boleh: ${boleh}.`;
  if (!(file.size > 0)) return "File kosong tidak bisa diunggah.";
  if (file.size > (batas?.maks_mb || 0) * MIB) return `File terlalu besar (${ukuranTeks(file.size)}). Paling besar ${ukuranTeks((batas?.maks_mb || 0) * MIB)}.`;
  return null;
}

export const LABEL_STATUS = {
  MENUNGGU_UNGGAH: ["Belum selesai", "border-amber-500/50 text-amber-300"],
  MENYATUKAN: ["Disatukan…", "border-amber-500/50 text-amber-300"],
  PROSES: ["Diproses…", "border-amber-500/50 text-amber-300"],
  DRAF: ["Draf", "border-sky-500/50 text-sky-300"],
  FINAL: ["Final", "border-emerald-500/60 text-emerald-300"],
  GAGAL: ["Gagal / dibatalkan", "border-slate-600 text-slate-400"],
  DITOLAK: ["Ditolak (isi bukan jenisnya)", "border-red-500/50 text-red-300"],
  DIHAPUS: ["Dihapus", "border-slate-600 text-slate-400"],
  KEDALUWARSA: ["Kedaluwarsa", "border-slate-600 text-slate-400"],
};
export const labelStatus = (s) => LABEL_STATUS[s] || [s || "—", "border-slate-600 text-slate-400"];
export const adaFile = (s) => s === "DRAF" || s === "FINAL";

// id of the newest FINAL version ("pakai ini"), or null
export function finalTerbaru(files) {
  let best = null;
  for (const f of files || []) if (f.status === "FINAL" && (!best || f.versi > best.versi)) best = f;
  return best ? best.id : null;
}

// part numbers 1..jumlah not yet in `sudah` ([{nomor}, ...]), ascending
export function sisaBagian(jumlah, sudah) {
  const ada = new Set((sudah || []).map((b) => b.nomor));
  const out = [];
  for (let n = 1; n <= jumlah; n++) if (!ada.has(n)) out.push(n);
  return out;
}

// [start, end) byte range of part `nomor` (1-based)
export function rentangBagian(nomor, ukuranBagian, ukuranFile) {
  const awal = (nomor - 1) * ukuranBagian;
  return [awal, Math.min(ukuranFile, awal + ukuranBagian)];
}

// wait before retry k (1, 2, 3): 1 s, 3 s, 9 s
export const jedaUlang = (k) => 1000 * 3 ** Math.max(0, k - 1);

// "8,4 MB/detik" ; null when nothing measurable yet
export function kecepatanTeks(byte, ms) {
  if (!(byte > 0 && ms > 500)) return null;
  return `${ukuranTeks((byte * 1000) / ms)}/detik`;
}

// remaining time as "±2 menit" / "±40 detik"; null when unknown
export function sisaWaktuTeks(sisaByte, byte, ms) {
  if (!(byte > 0 && ms > 500) || !(sisaByte >= 0)) return null;
  const detik = Math.ceil((sisaByte * ms) / byte / 1000);
  if (detik >= 90) return `±${Math.round(detik / 60)} menit`;
  return `±${Math.max(1, detik)} detik`;
}

// how long a finished upload took, in words ("16 detik", "3 menit 5 detik")
export function lamaUnggahTeks(detik) {
  const d = Math.round(Number(detik));
  if (!(d > 0)) return null;
  if (d < 60) return `${d} detik`;
  const m = Math.floor(d / 60), s = d % 60;
  return s ? `${m} menit ${s} detik` : `${m} menit`;
}

// roles that may open the file list (the server checks again: 403/404 for everyone else)
export const PEMBACA_FILE_MASTER = ["admin", "desain", "pattern_layout", "manager", "op_sublim", "op_dtf", "op_digital", "op_outdoor_finishing"];
export const bolehLihatFileMaster = (role) => PEMBACA_FILE_MASTER.includes(role);

export const LABEL_AKSI = {
  UNGGAH_MULAI: "Mulai mengunggah", UNGGAH_SELESAI: "Unggahan selesai", UNGGAH_BATAL: "Unggahan dibatalkan",
  DITOLAK_JENIS: "Ditolak: isi bukan jenisnya", TANDAI_FINAL: "Ditandai final", BATAL_FINAL: "Final dibatalkan",
  TAUTAN_UNDUH: "Mengunduh", HAPUS: "Dihapus", HAPUS_OTOMATIS: "Dihapus otomatis",
};
